import React, { useRef, useState } from "react";
import { PAYMENT_METHOD_OPTIONS } from "../../helpers/accountsReceivable";
import { CPDropdown } from "../CPDropdown";
import {
  assertAmountWithinRemaining,
  centsToDecimal,
  formatMoneyFromCents,
  summarizeInvoiceSettlement,
  toCents,
} from "../../helpers/invoiceMoney";
import { ViewportDialog } from "../shared/ViewportDialog";
import "../materials/materialRecord.css";
import "./RecordPaymentModal.css";

const INPUT_STYLE = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid rgba(201,168,76,0.25)",
  background: "rgba(255,255,255,0.04)",
  color: "white",
  fontSize: "15px",
  boxSizing: "border-box",
  marginTop: "6px",
};

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pay-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function RecordPaymentModal({
  invoice,
  saving,
  onCancel,
  onSubmit,
}) {
  const settlement = summarizeInvoiceSettlement(invoice);
  const [amount, setAmount] = useState(centsToDecimal(settlement.outstandingCents));
  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const [paymentMethod, setPaymentMethod] = useState("eft");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [error, setError] = useState("");
  const idempotencyKey = useRef(newIdempotencyKey());
  const submitLock = useRef(false);

  const handleSubmit = async () => {
    setError("");
    if (submitLock.current || saving) return;
    if (!paymentMethod) {
      setError("Select a payment method.");
      return;
    }
    if (!paymentDate) {
      setError("Enter a payment date.");
      return;
    }

    let amountCents;
    try {
      amountCents = toCents(amount);
      assertAmountWithinRemaining(
        settlement.totalCents,
        settlement.paidCents,
        amountCents,
      );
    } catch (validationError) {
      setError(validationError.message || "Enter a valid payment amount.");
      return;
    }

    submitLock.current = true;
    try {
      await onSubmit({
        amount: centsToDecimal(amountCents),
        paymentDate,
        paymentMethod,
        paymentReference: paymentReference.trim(),
        paymentNotes: paymentNotes.trim(),
        idempotencyKey: idempotencyKey.current,
      });
    } catch (submitError) {
      setError(submitError.message || "Failed to record payment.");
      submitLock.current = false;
    }
  };

  return (
    <ViewportDialog
      unstyled
      presentation="sheet"
      busy={saving}
      onClose={onCancel}
      titleId="record-payment-title"
      overlayClassName="pay-sheet-overlay"
      panelClassName="pay-sheet"
    >
        <h3 id="record-payment-title" style={{ marginTop: 0 }}>Record Payment</h3>
        <p style={{ opacity: 0.75, lineHeight: 1.5, marginBottom: 0 }}>
          {invoice?.invoice_number || "Invoice"}
        </p>
        <div className="invoice-payments__totals" style={{ marginTop: 12 }}>
          <div>
            <span>Invoice Total</span>
            <strong>{formatMoneyFromCents(settlement.totalCents)}</strong>
          </div>
          <div>
            <span>Previously Paid</span>
            <strong>{formatMoneyFromCents(settlement.paidCents)}</strong>
          </div>
          <div>
            <span>Remaining</span>
            <strong>{formatMoneyFromCents(settlement.outstandingCents)}</strong>
          </div>
        </div>

        <label style={{ display: "block", marginTop: "14px", fontSize: "13px" }}>
          Amount
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            style={INPUT_STYLE}
            aria-label="Payment amount"
          />
        </label>

        <label style={{ display: "block", marginTop: "14px", fontSize: "13px" }}>
          Payment Date
          <input
            type="date"
            value={paymentDate}
            onChange={(event) => setPaymentDate(event.target.value)}
            style={INPUT_STYLE}
          />
        </label>

        <div style={{ marginTop: "14px" }}>
          <CPDropdown
            label="Payment Method"
            value={paymentMethod}
            onChange={setPaymentMethod}
            includePlaceholder={false}
            options={PAYMENT_METHOD_OPTIONS}
          />
        </div>

        <label style={{ display: "block", marginTop: "14px", fontSize: "13px" }}>
          Reference Number
          <input
            type="text"
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.target.value)}
            placeholder="Cheque #, transaction id, etc."
            aria-label="Reference number"
            style={INPUT_STYLE}
          />
        </label>

        <label style={{ display: "block", marginTop: "14px", fontSize: "13px" }}>
          Notes
          <textarea
            value={paymentNotes}
            onChange={(event) => setPaymentNotes(event.target.value)}
            rows={3}
            placeholder="Optional notes..."
            aria-label="Payment notes"
            style={{ ...INPUT_STYLE, resize: "vertical" }}
          />
        </label>

        {error ? (
          <p style={{ color: "#e06b5a", marginTop: "12px" }}>{error}</p>
        ) : null}

        <div className="pay-sheet__actions">
          <button
            type="button"
            className="rec-btn rec-btn--ghost"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rec-btn rec-btn--primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
    </ViewportDialog>
  );
}
